import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, UserPlus, Clock, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface InviteAthleteModalProps {
  children: React.ReactNode;
}

export const InviteAthleteModal: React.FC<InviteAthleteModalProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const generateCode = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('generate-agent-code');
      
      if (error) throw error;
      
      setInviteCode(data.code);
      setExpiresAt(new Date(data.expires_at).toLocaleString());
      
      toast({
        title: "Invite code generated",
        description: "Share this code with the athlete to link their account.",
      });
    } catch (error) {
      console.error('Error generating code:', error);
      toast({
        title: "Error",
        description: "Failed to generate invite code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (inviteCode) {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Code copied",
        description: "Invite code copied to clipboard.",
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setInviteCode('');
      setExpiresAt('');
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Athlete
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Generate a secure invite code that athletes can use to link their account to yours.
          </p>
          
          {!inviteCode ? (
            <Button 
              onClick={generateCode} 
              disabled={loading}
              className="w-full"
            >
              {loading ? "Generating..." : "Generate Invite Code"}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Invite Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="code"
                    value={inviteCode}
                    readOnly
                    className="font-mono text-lg tracking-wider text-center"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyCode}
                    className="shrink-0"
                  >
                    {copied ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Expires: {expiresAt}</span>
              </div>
              
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm font-medium mb-1">Instructions for athlete:</p>
                <p className="text-sm text-muted-foreground">
                  Go to your profile and click "Link Agent", then enter this code to connect your account.
                </p>
              </div>
              
              <Button 
                onClick={generateCode} 
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                Generate New Code
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};